/**
 * ImpersonateContext — Mode "Voir en tant que" (DEV uniquement)
 *
 * Permet a un admin global de simuler la vue d'un autre utilisateur
 * sans changer la session OAuth. Frontend only, zero risque securite.
 * Active uniquement si REACT_APP_ENABLE_IMPERSONATE=true (build arg DEV).
 */

import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';

const ImpersonateContext = createContext(null);

const ENABLED = process.env.REACT_APP_ENABLE_IMPERSONATE === 'true';

export const ImpersonateProvider = ({ children, realUser }) => {
  const [impersonatedUser, setImpersonatedUser] = useState(null);

  const startImpersonating = useCallback((targetUser) => {
    if (!ENABLED || realUser?.role !== 'admin') return;
    setImpersonatedUser({
      ...realUser,
      // Override avec les permissions de l'utilisateur cible
      role: targetUser.role,
      modules: targetUser.modules,
      structures: targetUser.structures,
      // Garder le nom de la cible pour l'affichage
      _impersonatedName: targetUser.name || targetUser.email,
      _impersonatedRole: targetUser.role,
      _impersonatedUserId: targetUser.id,
    });
  }, [realUser]);

  const stopImpersonating = useCallback(() => {
    setImpersonatedUser(null);
  }, []);

  const isImpersonating = !!impersonatedUser;

  // Le user effectif : soit le user simule, soit le vrai user
  const effectiveUser = isImpersonating ? impersonatedUser : realUser;

  // Expose l'impersonation à l'intercepteur axios (api.js) pour propagation backend.
  // Le backend filtre ses réponses selon ces headers SI l'user réel est admin (sécurité serveur).
  useEffect(() => {
    if (isImpersonating) {
      window.__suiviplus_impersonate = {
        role: impersonatedUser.role,
        modules: impersonatedUser.modules,
        structures: impersonatedUser.structures,
      };
    } else {
      delete window.__suiviplus_impersonate;
    }
    return () => { delete window.__suiviplus_impersonate; };
  }, [isImpersonating, impersonatedUser]);

  return (
    <ImpersonateContext.Provider value={{
      enabled: ENABLED,
      realUser,
      effectiveUser,
      isImpersonating,
      impersonatedUser,
      startImpersonating,
      stopImpersonating,
    }}>
      {children}
    </ImpersonateContext.Provider>
  );
};

export const useImpersonate = () => {
  const ctx = useContext(ImpersonateContext);
  if (!ctx) throw new Error('useImpersonate must be used within ImpersonateProvider');
  return ctx;
};

export default ImpersonateContext;
